import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, ArrowDownCircle } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import { FacturaCombobox } from "@/components/FacturaCombobox";

export default function RetencionesRecibidas() {
  const [items, setItems] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() {
    return {
      cliente_id: "", factura_id: "",
      numero_comprobante: "",
      clave_acceso_sri: "", numero_autorizacion_sri: "",
      fecha: new Date().toISOString().slice(0, 10),
      base_ir: 0, porcentaje_ir: 1, valor_retenido_ir: 0,
      base_iva: 0, porcentaje_iva: 70, valor_retenido_iva: 0,
      observacion: "",
    };
  }
  const load = async () => {
    const [r, c] = await Promise.all([
      api.get("/retenciones-recibidas"),
      api.get("/masters/clientes"),
    ]);
    setItems(r.data); setClientes(c.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  // Cargar facturas SOLO cuando se selecciona un cliente — mucho más rápido y enfocado
  useEffect(() => {
    if (!form.cliente_id) { setFacturas([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/retenciones-recibidas/facturas-elegibles?cliente_id=${form.cliente_id}`);
        setFacturas(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [form.cliente_id]);

  const onClienteChange = (v) => {
    // Al cambiar de cliente, limpiar factura previa (puede no aplicar al nuevo)
    setForm(fm => ({ ...fm, cliente_id: v, factura_id: "", base_ir: 0, base_iva: 0, valor_retenido_ir: 0, valor_retenido_iva: 0 }));
  };

  const onFacturaChange = (v) => {
    const f = facturas.find(x => x.id === v);
    if (f) {
      const sub = f.subtotal || 0;
      const iva = f.iva || 0;
      setForm(fm => ({
        ...fm, factura_id: v, cliente_id: f.cliente_id || fm.cliente_id,
        base_ir: sub, base_iva: iva,
        valor_retenido_ir: +(sub * fm.porcentaje_ir / 100).toFixed(2),
        valor_retenido_iva: +(iva * fm.porcentaje_iva / 100).toFixed(2),
      }));
    }
  };

  const onPorcIr = (v) => setForm(f => ({ ...f, porcentaje_ir: v, valor_retenido_ir: +((f.base_ir || 0) * v / 100).toFixed(2) }));
  const onPorcIva = (v) => setForm(f => ({ ...f, porcentaje_iva: v, valor_retenido_iva: +((f.base_iva || 0) * v / 100).toFixed(2) }));
  const totalRet = useMemo(() => +((form.valor_retenido_ir || 0) + (form.valor_retenido_iva || 0)).toFixed(2), [form]);

  const save = async () => {
    if (!form.cliente_id || !form.factura_id || !form.numero_comprobante) return toast.error("Complete cliente, factura y número del comprobante");
    if (totalRet <= 0) return toast.error("La retención debe tener al menos un valor > 0");
    try { await api.post("/retenciones-recibidas", form); toast.success("Retención registrada · saldo de factura actualizado"); setOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const eliminar = async (r) => {
    if (!window.confirm(`¿Eliminar retención ${r.numero_interno}? El saldo de la factura se restaurará.`)) return;
    try { await api.delete(`/retenciones-recibidas/${r.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const totalHist = useMemo(() => items.reduce((s, x) => s + (x.total_retenido || 0), 0), [items]);

  return (
    <div className="space-y-6" data-testid="ret-recibidas-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Retenciones recibidas</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Comprobantes de retención emitidos por clientes (especialmente instituciones públicas) al pagar facturas. Reducen automáticamente el saldo pendiente en cuentas por cobrar.</p>
          <div className="mt-3 text-xs text-[#5c7368]">Total histórico retenido: <strong className="text-[#15241d] tabular-nums">${totalHist.toFixed(2)}</strong></div>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#205c45]" data-testid="new-ret-btn"><Plus size={16} className="mr-2" />Nueva retención recibida</Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Factura</th><th>Comprobante</th><th className="text-right">IR</th><th className="text-right">IVA</th><th className="text-right">Total</th><th className="w-12"></th></tr></thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id}>
                <td className="font-mono text-xs font-semibold">{r.numero_interno}</td>
                <td>{r.fecha}</td>
                <td>{r.cliente_nombre}</td>
                <td className="font-mono text-xs">{r.factura_numero}</td>
                <td className="font-mono text-xs">{r.numero_comprobante}</td>
                <td className="text-right tabular-nums">${(r.valor_retenido_ir || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums">${(r.valor_retenido_iva || 0).toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold text-[#7a2514]">${(r.total_retenido || 0).toFixed(2)}</td>
                <td><Button size="icon" variant="ghost" onClick={() => eliminar(r)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin retenciones registradas. Cuando una institución pública (ej. JBG) le pague y retenga parte, registre el comprobante aquí.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva retención recibida</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Cliente <span className="text-[#b53a25]">*</span></Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={onClienteChange}
                clientes={clientes}
                placeholder="Primero busque y seleccione el cliente…"
                testId="retrec-cliente"
              />
              {form.cliente_id && (
                <p className="text-[11px] text-[#5c7368] mt-1">
                  {facturas.length === 0
                    ? "Sin facturas elegibles para este cliente (todas pagadas con retención previa o sin facturas autorizadas)."
                    : `${facturas.length} factura(s) elegibles para retención.`}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label>Factura de venta autorizada <span className="text-[#b53a25]">*</span></Label>
              <FacturaCombobox
                value={form.factura_id}
                onChange={onFacturaChange}
                facturas={facturas}
                placeholder={form.cliente_id ? "Buscar factura por número o monto…" : "Seleccione primero un cliente arriba ↑"}
                testId="ret-factura"
                disabled={!form.cliente_id}
              />
            </div>
            <div><Label>Fecha comprobante</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>N° comprobante</Label><Input value={form.numero_comprobante} onChange={(e) => setForm({ ...form, numero_comprobante: e.target.value })} placeholder="001-001-000000123" /></div>
            <div><Label>Autorización SRI (opc.)</Label><Input value={form.numero_autorizacion_sri} onChange={(e) => setForm({ ...form, numero_autorizacion_sri: e.target.value })} /></div>
            <div className="col-span-2"><Label>Clave acceso SRI (opc.)</Label><Input value={form.clave_acceso_sri} onChange={(e) => setForm({ ...form, clave_acceso_sri: e.target.value })} className="font-mono text-xs" /></div>
          </div>
          <div className="mt-4 rounded-md border border-[#d1dbd6] p-3 bg-[#f7f9f8]">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Retención en la fuente (IR)</div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Base</Label><Input type="number" step="0.01" value={form.base_ir} onChange={(e) => setForm({ ...form, base_ir: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>%</Label><Input type="number" step="0.01" value={form.porcentaje_ir} onChange={(e) => onPorcIr(parseFloat(e.target.value) || 0)} /></div>
              <div><Label>Valor retenido</Label><Input type="number" step="0.01" value={form.valor_retenido_ir} onChange={(e) => setForm({ ...form, valor_retenido_ir: parseFloat(e.target.value) || 0 })} className="font-semibold" /></div>
            </div>
          </div>
          <div className="rounded-md border border-[#d1dbd6] p-3 bg-[#f7f9f8]">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Retención IVA</div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Base (IVA factura)</Label><Input type="number" step="0.01" value={form.base_iva} onChange={(e) => setForm({ ...form, base_iva: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>%</Label><Input type="number" step="0.01" value={form.porcentaje_iva} onChange={(e) => onPorcIva(parseFloat(e.target.value) || 0)} /></div>
              <div><Label>Valor retenido</Label><Input type="number" step="0.01" value={form.valor_retenido_iva} onChange={(e) => setForm({ ...form, valor_retenido_iva: parseFloat(e.target.value) || 0 })} className="font-semibold" /></div>
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#e9f2ee] border border-[#b2d0c2] rounded-md px-4 py-2 mt-3">
            <span className="text-sm font-semibold text-[#205c45] flex items-center gap-2"><ArrowDownCircle size={16} />Total retenido (se descontará del saldo de la factura)</span>
            <span className="text-xl font-bold tabular-nums text-[#205c45]">${totalRet.toFixed(2)}</span>
          </div>
          <div><Label>Observación</Label><Input value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-ret-btn"><Receipt size={16} className="mr-2" />Registrar retención</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
